/**
 * Zutaten in der Draufsicht mit eigenem, wiedererkennbarem Umriss.
 *
 * Jede Zutat wird in ein Rechteck gezeichnet. Der Umriss dient auch als
 * Beschnitt, wenn die Zutat beim Schneiden in Stücke zerfällt.
 */
import { random } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import type { Tone } from '../palette.ts';
import { blobPoints, ellipsePath, path, smooth } from '../paths.ts';
import { circle, line, shape } from '../scene.ts';
import type { SceneNode } from '../scene.ts';

/** Lage einer Zutat. */
export interface Box {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/** Gezeichnete Zutat. */
export interface Produce {
  /** Umriss, auch als Beschnitt nutzbar. */
  readonly outline: string;
  readonly fill: Tone;
  /** Innere Zeichnung (Rippen, Augen, Riefen). */
  readonly details: readonly SceneNode[];
  /** Dinge außerhalb des Umrisses, zum Beispiel Wurzelhaare. */
  readonly extras: readonly SceneNode[];
  /** Breite eines Endstücks, das beim Würfeln ganz bleibt (Wurzelansatz). */
  readonly keepRight: number;
  /** Fläche der Schnittstücke. */
  readonly pieceFill: Tone;
}

/** Bekannte Zutaten zum Schneiden. */
export const PRODUCE_NAMES = {
  onion: 'Zwiebel',
  potato: 'Kartoffel',
  carrot: 'Möhre',
  cucumber: 'Gurke',
} as const;

/** Kennung einer Zutat. */
export type ProduceKind = keyof typeof PRODUCE_NAMES;

/** Feine Linie. */
const FINE = (stroke: Tone): { stroke: Tone; strokeWidth: number } => ({ stroke, strokeWidth: STROKE.fine });

/** Halbe Zwiebel, Schnittfläche unten: Spitze links, Wurzel rechts, Längsrippen. */
function onion(box: Box): Produce {
  const { x, y, width: w, height: h } = box;
  const cy = y + h / 2;
  const root = x + w - 5;
  const outline = path()
    .move(x, cy)
    .curve(x + w * 0.12, cy - h * 0.38, x + w * 0.42, y, x + w * 0.62, y)
    .curve(x + w * 0.84, y, root, cy - h * 0.3, root, cy - h * 0.18)
    .line(root, cy + h * 0.18)
    .curve(root, cy + h * 0.3, x + w * 0.84, y + h, x + w * 0.62, y + h)
    .curve(x + w * 0.42, y + h, x + w * 0.12, cy + h * 0.38, x, cy)
    .close()
    .toString();
  const details: SceneNode[] = [-0.62, -0.32, 0, 0.32, 0.62].map((f) =>
    shape(
      path()
        .move(x + w * 0.06, cy)
        .quad(x + w * 0.5, cy + f * h * 1.02, root - 2, cy + f * h * 0.3)
        .toString(),
      FINE('shade'),
    ),
  );
  const extras: SceneNode[] = [-6, -2, 2, 6].map((dy) =>
    line(root + 1, cy + dy, root + 6, cy + dy * 1.4, FINE('deep')),
  );
  return { outline, fill: 'paper', details, extras, keepRight: w * 0.12, pieceFill: 'paper' };
}

/** Kartoffel: knubbelige Form mit Augen. */
function potato(box: Box): Produce {
  const { x, y, width: w, height: h } = box;
  const rng = random(41);
  const bumps = Array.from({ length: 12 }, () => 0.94 + rng() * 0.08);
  const outline = smooth(
    blobPoints(x + w / 2, y + h / 2, w / 2, h / 2, 12, (_angle, index) => bumps[index] ?? 1),
    true,
  );
  const eyes: readonly (readonly [number, number])[] = [
    [0.28, 0.35],
    [0.62, 0.28],
    [0.72, 0.66],
    [0.4, 0.7],
  ];
  const details: SceneNode[] = eyes.flatMap(([fx, fy]) => {
    const ex = x + w * fx;
    const ey = y + h * fy;
    return [
      shape(
        path()
          .move(ex - 3, ey)
          .quad(ex, ey - 2.5, ex + 3, ey)
          .toString(),
        FINE('deep'),
      ),
      circle(ex, ey + 1.2, 0.9, { fill: 'deep', stroke: 'none' }),
    ];
  });
  return { outline, fill: 'shade', details, extras: [], keepRight: 0, pieceFill: 'paper' };
}

/** Möhre: spitz zulaufend, mit Querriefen und Stielansatz rechts. */
function carrot(box: Box): Produce {
  const { x, y, width: w, height: h } = box;
  const cy = y + h / 2;
  const right = x + w - 4;
  const outline = path()
    .move(x, cy)
    .curve(x + w * 0.3, cy - h * 0.32, x + w * 0.7, y, right - 3, y)
    .quad(right, y, right, y + 3)
    .line(right, y + h - 3)
    .quad(right, y + h, right - 3, y + h)
    .curve(x + w * 0.7, y + h, x + w * 0.3, cy + h * 0.32, x, cy)
    .close()
    .toString();
  const details: SceneNode[] = [0.22, 0.38, 0.55, 0.72, 0.86].flatMap((f) => {
    const rx = x + w * f;
    const half = (h / 2) * Math.min(1, 0.35 + f * 0.75) * 0.55;
    return [
      line(rx, cy - half, rx + 3, cy - half * 0.3, FINE('deep')),
      line(rx + 6, cy + half * 0.2, rx + 9, cy + half * 0.9, FINE('deep')),
    ];
  });
  details.push(shape(ellipsePath(right - 1, cy, 2.2, h * 0.32), { fill: 'tint', ...FINE('deep') }));
  return { outline, fill: 'shade', details, extras: [], keepRight: w * 0.06, pieceFill: 'shade' };
}

/** Gurke: lang, mit hellen Punkten auf dunkler Schale. */
function cucumber(box: Box): Produce {
  const { x, y, width: w, height: h } = box;
  const r = h / 2;
  const outline = path()
    .move(x + r, y)
    .line(x + w - r, y + 1)
    .arc(r - 1, r - 1, false, true, x + w - r, y + h - 1)
    .line(x + r, y + h)
    .arc(r, r, false, true, x + r, y)
    .close()
    .toString();
  const rng = random(17);
  const details: SceneNode[] = [
    line(x + r, y + h * 0.3, x + w - r, y + h * 0.3, { stroke: 'shade', strokeWidth: STROKE.detail }),
  ];
  for (let index = 0; index < 16; index += 1) {
    details.push(
      circle(x + r + rng() * (w - 2 * r), y + 5 + rng() * (h - 10), 0.9, { fill: 'paper', stroke: 'none' }),
    );
  }
  return { outline, fill: 'deep', details, extras: [], keepRight: 0, pieceFill: 'tint' };
}

/** Zeichnet eine Zutat in ein Rechteck. */
export function produce(kind: ProduceKind, box: Box): Produce {
  switch (kind) {
    case 'potato':
      return potato(box);
    case 'carrot':
      return carrot(box);
    case 'cucumber':
      return cucumber(box);
    default:
      return onion(box);
  }
}

/** Ganze Zutat als Knoten: Extras, Fläche, Innenzeichnung im Umriss, Umriss. */
export function produceNodes(item: Produce): SceneNode[] {
  return [
    ...item.extras,
    shape(item.outline, { fill: item.fill, stroke: 'none' }),
    { kind: 'group', children: item.details, clip: item.outline },
    shape(item.outline, { stroke: 'ink', strokeWidth: STROKE.outline }),
  ];
}

/**
 * Schnittfläche einer Scheibe in der Draufsicht, also das, was man sieht,
 * wenn die Scheibe umgefallen ist: Gurke mit Kernen, Möhre mit hellem Kern,
 * Kartoffel hell, Zwiebel mit Ringen.
 */
export function crossSection(kind: ProduceKind, cx: number, cy: number, r: number): SceneNode[] {
  const disc = ellipsePath(cx, cy, r, r);
  switch (kind) {
    case 'cucumber': {
      const seeds: SceneNode[] = [0, 120, 240].flatMap((angle) => {
        const radians = (angle * Math.PI) / 180;
        return [0.28, 0.5].map((f) =>
          circle(cx + Math.cos(radians) * r * f, cy + Math.sin(radians) * r * f, r * 0.09, {
            fill: 'paper',
            stroke: 'deep',
            strokeWidth: STROKE.fine,
          }),
        );
      });
      return [
        shape(disc, { fill: 'deep', stroke: 'ink', strokeWidth: STROKE.detail }),
        circle(cx, cy, r * 0.82, { fill: 'tint', stroke: 'none' }),
        circle(cx, cy, r * 0.5, { fill: 'shade', stroke: 'none' }),
        ...seeds,
      ];
    }
    case 'carrot':
      return [
        shape(disc, { fill: 'shade', stroke: 'ink', strokeWidth: STROKE.detail }),
        circle(cx, cy, r * 0.45, { fill: 'tint', stroke: 'deep', strokeWidth: STROKE.fine }),
      ];
    case 'onion':
      return [
        shape(disc, { fill: 'paper', stroke: 'ink', strokeWidth: STROKE.detail }),
        circle(cx, cy, r * 0.7, { fill: 'none', stroke: 'shade', strokeWidth: STROKE.fine }),
        circle(cx, cy, r * 0.42, { fill: 'none', stroke: 'shade', strokeWidth: STROKE.fine }),
      ];
    default:
      return [
        shape(disc, { fill: 'paper', stroke: 'ink', strokeWidth: STROKE.detail }),
        circle(cx, cy, r * 0.72, { fill: 'none', stroke: 'tint', strokeWidth: STROKE.fine }),
      ];
  }
}
